import { useTimezone } from '@app/core/hooks/useTimezone';
import { Box, Skeleton } from '@mui/material';
import { YoutubeStatsChartFetchOverlay } from './YoutubeStatsChartFetchOverlay';
import { NullState } from '@ui/components';
import moment from 'moment-timezone';
import { useEffect, useMemo, useRef, useState } from 'react';
import { defineMessage, useIntl } from 'react-intl';
import { AlignedData, Options } from 'uplot';
import Chart from '../../../graph/LineGraph';
import { pastelColors } from '../../transcriptions/utils';
import { stationsEnum } from '../helpers';
import {
  transformViewersStatsForUplot,
  ViewersStatsForChart,
} from './transform-viewers-stats-for-uplot';
import { YoutubeStatsToolbarV2 } from './YoutubeStatsToolbarV2';

const noDataMessage = defineMessage({
  id: 'logger.youtubeStats.noDataForPeriod',
  defaultMessage: 'No hay datos disponibles para este período',
});

export type YoutubeStatsViewV2Props = {
  stats: ViewersStatsForChart | undefined;
  isLoading: boolean;
  /** True while the BFF stats request is in flight (including background refetch). */
  isStatsFetching: boolean;
  stationsSelection: string;
  setStationsSelection: (stationsSelection: string) => void;
  stationsList: { channelId: string; channelName: string }[];
  selectedStations: string[];
  setSelectedStations: (selectedStations: string[]) => void;
  stationYoutubeChannelId?: string | null;
  dateRange: { label: string; range: { from: string; to: string } };
  dateRanges: { label: string; range: { from: string; to: string } }[];
  handleDateRangeChange: (selectedLabel: string) => void;
};

/**
 * YouTube viewer chart fed by the BFF country catalog payload. Series visibility is derived locally so
 * changing scope or selected channels does not refetch.
 */
export function YoutubeStatsViewV2(props: YoutubeStatsViewV2Props) {
  const {
    stats,
    isLoading,
    isStatsFetching,
    dateRange,
    dateRanges,
    handleDateRangeChange,
    stationsSelection,
    setStationsSelection,
    stationsList,
    selectedStations,
    setSelectedStations,
    stationYoutubeChannelId,
  } = props;

  const { formatMessage } = useIntl();
  const tz = useTimezone();

  const visibleChannelIds = useMemo(() => {
    if (!stats?.metadata?.channels) {
      return [];
    }
    const available = Object.keys(stats.metadata.channels);
    if (stationsSelection === stationsEnum.all) {
      return available;
    }
    if (stationsSelection === stationsEnum.current) {
      const selfIds = available.filter((id) => stats.metadata.channels[id]?.isSelf);
      if (selfIds.length > 0) {
        return selfIds;
      }
      if (stationYoutubeChannelId && available.includes(stationYoutubeChannelId)) {
        return [stationYoutubeChannelId];
      }
      return [];
    }
    return selectedStations.filter((id) => available.includes(id));
  }, [stats, stationsSelection, selectedStations, stationYoutubeChannelId]);

  const transformedData = useMemo(() => {
    return transformViewersStatsForUplot(stats, visibleChannelIds, tz, pastelColors);
  }, [stats, visibleChannelIds, tz]);

  const containerRef = useRef<HTMLDivElement>(null);
  const [divWidth, setDivWidth] = useState(0);

  useEffect(() => {
    if (containerRef.current) {
      setDivWidth(containerRef.current.offsetWidth);
    }
  }, []);

  const opts: Options = {
    width: divWidth,
    height: 640,
    series: [{}, ...transformedData.series],
    legend: {
      markers: {
        fill: (u, seriesIdx) => {
          const stroke = u.series[seriesIdx]?.stroke;
          if (typeof stroke === 'function') {
            const result = stroke(u, seriesIdx);
            if (typeof result === 'string') {
              return result;
            }
          }
          return 'transparent';
        },
      },
    },
    axes: [
      {
        space: 40,
        incrs: [1, 5, 10, 15, 30, 60, 60 * 2, 60 * 3, 60 * 6, 60 * 12, 60 * 24, 60 * 48],
        values: (u, vals) => {
          return vals.map((v) => {
            const momentTime = moment.unix(v).tz(tz);
            return momentTime.format('HH:mm');
          });
        },
      },
      {
        space: 40,
        size: 60,
      },
    ],
  };

  const hasSeriesData =
    Array.isArray(transformedData.data[0]) && (transformedData.data[0] as number[]).length > 0;

  const showInitialSkeleton = isLoading && !stats;

  return (
    <Box
      ref={containerRef}
      sx={{
        display: 'flex',
        gap: 2,
        mb: 2,
        justifyContent: 'center',
        flexDirection: 'column',
      }}
    >
      <YoutubeStatsToolbarV2
        stationsSelection={stationsSelection}
        setStationsSelection={setStationsSelection}
        stationsList={stationsList}
        selectedStations={selectedStations}
        setSelectedStations={setSelectedStations}
        dateRange={dateRange}
        dateRanges={dateRanges}
        handleDateRangeChange={handleDateRangeChange}
      />
      {showInitialSkeleton ? (
        <Box>
          <Skeleton variant="rectangular" width="100%" height={640} />
        </Box>
      ) : hasSeriesData ? (
        <Box sx={{ position: 'relative', minHeight: 640 }}>
          <Chart
            options={opts}
            data={transformedData.data as AlignedData}
            width=""
            height=""
            timezone={tz}
          />
          {isStatsFetching ? <YoutubeStatsChartFetchOverlay /> : null}
        </Box>
      ) : (
        <NullState className="p-7">{formatMessage(noDataMessage)}</NullState>
      )}
    </Box>
  );
}
